/**
 * Native app update notice — informational only.
 *
 * The configuration (latest version, message, per-platform store URL) is
 * served by the `get-app-update-policy` edge function and backed by the
 * `app_version_policy` table, so a new release can be announced without
 * shipping a new build.
 *
 * Design rules (non-negotiable):
 *  - Three states: current / optional / required. "required" only when the
 *    policy explicitly sets mandatory=true AND minimumVersion > installed
 *    AND minimumVersion <= latestVersion AND the installed version came from
 *    the native shell AND the policy was confirmed by the server within
 *    REQUIRED_CACHE_MAX_AGE_MS. Anything else fails open (never blocks).
 *  - No dependency on authentication. Never reloads, never clears storage.
 *  - A failed / missing / malformed configuration is silently ignored.
 *  - "Update Now" opens the store directly from the client. It never calls an
 *    edge function and never needs connectivity to Supabase at tap time.
 *  - If no valid store URL is known (remote or local fallback), no prompt is
 *    shown at all.
 */
import { APP_VERSION } from "@/lib/appVersion";
import { logger } from "@/lib/logger";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/lib/mobileAppBanner";

export type UpdatePlatform = "ios" | "android";
export type UpdateStatus = "current" | "optional" | "required";

export interface AppUpdatePolicy {
  platform: UpdatePlatform;
  latestVersion: string;
  updateMessage: string | null;
  /** Remote store URL when configured and valid; otherwise null. */
  storeUrl: string | null;
  /** Minimum supported version; only enforced when `mandatory` is true. */
  minimumVersion?: string | null;
  /** Master switch for blocking. */
  mandatory?: boolean;
}

/** A cached "required" decision is only honoured for this long without a fresh server confirmation. */
export const REQUIRED_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface EvaluateOptions {
  /** True only when the installed version was read from the native shell (not the bundled fallback). */
  nativeVersionKnown?: boolean;
  /** When the policy was last confirmed by a successful server fetch (ms epoch). */
  confirmedAt?: number | null;
  now?: number;
}

export const POLICY_CACHE_KEY = "tm_app_update_policy";
export const LATER_DISMISSED_KEY = "tm_app_update_dismissed";

/** Local fallback store URLs — single source of truth lives in mobileAppBanner. */
export const FALLBACK_STORE_URLS: Record<UpdatePlatform, string> = {
  ios: APP_STORE_URL,
  android: PLAY_STORE_URL,
};

export const DEFAULT_UPDATE_MESSAGE =
  "Please update TransportMate to get the latest improvements and fixes.";

/* ------------------------------------------------------------------ */
/* Version comparison                                                   */
/* ------------------------------------------------------------------ */

/** Returns the numeric segments of a version string, or null if unusable. */
export function parseVersion(value: unknown): number[] | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Accept 1, 1.54, 1.54.2, 1.54.2-beta (build metadata ignored)
  if (!/^\d+(\.\d+)*([-+].*)?$/.test(trimmed)) return null;
  const core = trimmed.split(/[-+]/)[0];
  const parts = core.split(".").map((p) => Number.parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n) || n < 0)) return null;
  return parts;
}

/** -1 | 0 | 1, or null when either version cannot be parsed. Numeric, not string. */
export function compareVersions(a: unknown, b: unknown): number | null {
  const left = parseVersion(a);
  const right = parseVersion(b);
  if (!left || !right) return null;
  const len = Math.max(left.length, right.length);
  for (let i = 0; i < len; i++) {
    const l = left[i] ?? 0;
    const r = right[i] ?? 0;
    if (l !== r) return l < r ? -1 : 1;
  }
  return 0;
}

/* ------------------------------------------------------------------ */
/* Policy normalisation                                                 */
/* ------------------------------------------------------------------ */

const isValidStoreUrl = (value: unknown): value is string =>
  typeof value === "string" && /^https:\/\/\S+$/.test(value.trim());

/** Returns a usable policy, or null when the configuration is malformed. */
export function normalisePolicy(raw: unknown): AppUpdatePolicy | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const platform = r.platform === "ios" || r.platform === "android" ? r.platform : null;
  if (!platform) return null;
  if (!parseVersion(r.latestVersion)) return null;
  return {
    platform,
    latestVersion: String(r.latestVersion).trim(),
    updateMessage:
      typeof r.updateMessage === "string" && r.updateMessage.trim()
        ? r.updateMessage.trim()
        : null,
    storeUrl: isValidStoreUrl(r.storeUrl) ? r.storeUrl.trim() : null,
    minimumVersion: parseVersion(r.minimumVersion) ? String(r.minimumVersion).trim() : null,
    mandatory: r.mandatory === true,
  };
}

/**
 * Store URL used by "Update Now": valid remote URL, else the local fallback,
 * else null (in which case no prompt is shown).
 */
export function resolveStoreUrl(policy: AppUpdatePolicy | null): string | null {
  if (!policy) return null;
  if (isValidStoreUrl(policy.storeUrl)) return policy.storeUrl;
  const fallback = FALLBACK_STORE_URLS[policy.platform];
  return isValidStoreUrl(fallback) ? fallback : null;
}

/**
 * Decide what to show. Unknown/malformed input, an equal version, a newer
 * installed version or a missing store URL all mean "current" (no prompt).
 */
export function evaluateUpdateStatus(
  installedVersion: unknown,
  policy: AppUpdatePolicy | null,
  opts: EvaluateOptions = {},
): UpdateStatus {
  if (!policy) return "current";
  if (!parseVersion(installedVersion)) return "current";
  if (!resolveStoreUrl(policy)) return "current";
  const vsLatest = compareVersions(installedVersion, policy.latestVersion);
  const base: UpdateStatus = vsLatest !== null && vsLatest < 0 ? "optional" : "current";
  if (base === "current") return "current";
  if (isRequired(installedVersion, policy, opts)) return "required";
  return base;
}

function isRequired(installed: unknown, policy: AppUpdatePolicy, opts: EvaluateOptions): boolean {
  if (policy.mandatory !== true || !policy.minimumVersion) return false;
  if (opts.nativeVersionKnown !== true) return false;
  // Misconfiguration guard: minimum above latest is never enforced.
  const minVsLatest = compareVersions(policy.minimumVersion, policy.latestVersion);
  if (minVsLatest === null || minVsLatest > 0) return false;
  const confirmedAt = opts.confirmedAt;
  if (typeof confirmedAt !== "number" || !Number.isFinite(confirmedAt)) return false;
  const now = opts.now ?? Date.now();
  if (now - confirmedAt > REQUIRED_CACHE_MAX_AGE_MS) return false;
  const vsMin = compareVersions(installed, policy.minimumVersion);
  return vsMin !== null && vsMin < 0;
}

/* ------------------------------------------------------------------ */
/* Cache                                                                */
/* ------------------------------------------------------------------ */

export function readCachedPolicy(): AppUpdatePolicy | null {
  try {
    const raw = localStorage.getItem(POLICY_CACHE_KEY);
    if (!raw) return null;
    return normalisePolicy(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** When the cached policy was last confirmed by the server (ms epoch), or null. */
export function readCachedConfirmedAt(): number | null {
  try {
    const raw = localStorage.getItem(POLICY_CACHE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw)?.confirmedAt;
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeCachedPolicy(policy: AppUpdatePolicy, confirmedAt: number = Date.now()): void {
  try {
    localStorage.setItem(POLICY_CACHE_KEY, JSON.stringify({ ...policy, confirmedAt }));
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/* ------------------------------------------------------------------ */
/* Installed version + platform                                         */
/* ------------------------------------------------------------------ */

export async function getUpdatePlatform(): Promise<UpdatePlatform | null> {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (!Capacitor.isNativePlatform()) return null;
    const p = Capacitor.getPlatform();
    return p === "ios" || p === "android" ? p : null;
  } catch {
    return null;
  }
}

/** Installed version read from the native shell only; null when unavailable. */
export async function getNativeInstalledVersion(): Promise<string | null> {
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    if (info?.version && parseVersion(info.version)) return info.version;
  } catch {
    /* web or plugin unavailable */
  }
  return null;
}

/** Installed native version, falling back to the bundled build version. */
export async function getInstalledVersion(): Promise<string> {
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    if (info?.version && parseVersion(info.version)) return info.version;
  } catch {
    /* web or plugin unavailable */
  }
  return APP_VERSION;
}

/* ------------------------------------------------------------------ */
/* Fetch                                                                */
/* ------------------------------------------------------------------ */

const FETCH_TIMEOUT_MS = 8000;

/**
 * Fetch the live configuration. On any failure (offline, timeout, malformed)
 * the cached configuration is used if it is still valid, otherwise nothing is
 * shown. This never throws and never blocks startup.
 */
export async function fetchUpdatePolicy(
  platform: UpdatePlatform,
): Promise<{ policy: AppUpdatePolicy | null; fromCache: boolean; confirmedAt: number | null }> {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!baseUrl) return { policy: readCachedPolicy(), fromCache: true, confirmedAt: readCachedConfirmedAt() };
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
    : null;
  try {
    const response = await fetch(
      `${baseUrl}/functions/v1/get-app-update-policy?platform=${platform}`,
      {
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        signal: controller?.signal,
      },
    );
    if (!response.ok) throw new Error(`status ${response.status}`);
    const policy = normalisePolicy(await response.json());
    if (!policy) {
      // Malformed remote config — ignore it entirely.
      logger.warn("[AppUpdate] Malformed version policy — ignoring");
      return { policy: null, fromCache: false, confirmedAt: null };
    }
    const confirmedAt = Date.now();
    writeCachedPolicy(policy, confirmedAt);
    return { policy, fromCache: false, confirmedAt };
  } catch (e) {
    logger.debug("[AppUpdate] Policy fetch failed, using cache:", e);
    return { policy: readCachedPolicy(), fromCache: true, confirmedAt: readCachedConfirmedAt() };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/* ------------------------------------------------------------------ */
/* Session dismissal ("Maybe Later")                                    */
/* ------------------------------------------------------------------ */

export function isDismissedThisSession(latestVersion: string): boolean {
  try {
    return sessionStorage.getItem(LATER_DISMISSED_KEY) === latestVersion;
  } catch {
    return false;
  }
}

export function dismissForSession(latestVersion: string): void {
  try {
    sessionStorage.setItem(LATER_DISMISSED_KEY, latestVersion);
  } catch {
    /* non-fatal */
  }
}

/* ------------------------------------------------------------------ */
/* Launching the update                                                 */
/* ------------------------------------------------------------------ */

async function openStoreListing(url: string): Promise<void> {
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url });
  } catch {
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      // Never surface a technical error or interrupt the user.
      logger.debug("[AppUpdate] Failed to open store listing:", e);
    }
  }
}

/**
 * Start the update — client-side only, no edge function, no Supabase call.
 *
 * Android: Google Play In-App Update (flexible) when available, otherwise the
 * Play listing. iOS (and any failure): the App Store listing.
 */
export async function startAppUpdate(
  policy: AppUpdatePolicy,
  opts: { immediate?: boolean } = {},
): Promise<"in_app" | "store" | "unavailable"> {
  const url = resolveStoreUrl(policy);
  if (!url) return "unavailable";

  if (policy.platform === "android") {
    try {
      const mod: any = await import(
        /* @vite-ignore */ "@capawesome/capacitor-app-update"
      );
      const plugin = mod?.AppUpdate;
      if (plugin) {
        const info = await plugin.getAppUpdateInfo();
        if (opts.immediate && info?.updateAvailability === 2 && info?.immediateUpdateAllowed) {
          await plugin.performImmediateUpdate();
          return "in_app";
        }
        if (info?.updateAvailability === 2 && info?.flexibleUpdateAllowed) {
          await plugin.startFlexibleUpdate();
          try {
            await plugin.completeFlexibleUpdate();
          } catch {
            /* completion happens on next foreground */
          }
          return "in_app";
        }
      }
    } catch (e) {
      logger.debug("[AppUpdate] In-app update unavailable, opening store:", e);
    }
  }

  await openStoreListing(url);
  return "store";
}
